export type Theme = 'light' | 'dark';

// Cross-referenced with the raw `'billing_theme'` literal in the no-flash
// script in index.html:<head> — that script runs before this module can
// load, so it can't import STORAGE_KEY and has to duplicate the string.
// If you rename this key, update index.html too, or the pre-paint script
// and the app disagree on where the theme is stored and the dark-mode
// flash this exists to prevent comes back.
export const STORAGE_KEY = 'billing_theme';

/**
 * Warm "Operational" palette: stone neutrals, teal accent.
 * Accent is teal-700, not teal-600 — teal-600 against white is 3.74:1 and
 * fails AA for normal text.
 *
 * Pairing rule (enforced by src/test/contrast.test.ts): `accent` as a
 * background pairs ONLY with `accentFg` text. `text`/`textMuted` are for the
 * neutral surfaces only — never on `accent`. `accentFg` is for `accent`
 * only — never on a neutral surface. `accent` may itself be used as text on
 * any neutral surface (e.g. a teal link or icon).
 *
 * `warning`/`warningFg` follow the same closed pairing as accent/accentFg
 * (Button's `warning` variant: `bg-warning text-warning-fg`, never mixed
 * with `text`/`textMuted`/`accentFg`) but are intentionally NOT theme-varied
 * like accent is. `warning` is only ever an opaque Button fill compared
 * against its own `warningFg` text — it's never rendered as plain text on a
 * neutral surface the way `accent` is (`text-accent` links/icons), which is
 * the reason `accent` needs a different shade per theme in the first place.
 * A single amber-700/white pair (5.02:1) clears AA regardless of the
 * surrounding theme, so light and dark share the same values.
 *
 * `badge*Bg`/`badge*Fg` mirror every tone in `ui/Badge.tsx`'s `TONES` map
 * (neutral/success/warning/danger/info/purple/orange). Unlike the tokens
 * above, these are NOT CSS custom properties — Badge renders literal
 * Tailwind utility classes (e.g. `bg-teal-100 dark:bg-teal-900`) straight
 * from the fixed palette, not `var(--...)`, so there is nothing to add to
 * index.css. They're hand-copied here from `tailwindcss/colors` (verified
 * against the installed version) purely so contrast.test.ts can police
 * every tone's own text/bg AA ratio AND every tone's dark bg against
 * `surfaceRaised` dark (a Badge's most common host is a Card — a tone whose
 * dark fill matches the card behind it renders as invisible floating text,
 * which is exactly the bug `neutral` shipped with: `dark:bg-stone-800` was
 * byte-identical to `surfaceRaised` dark, 1.00:1, before Badge had any
 * consumers). If `ui/Badge.tsx`'s TONES map changes, these must be
 * recomputed by hand to match — nothing derives them automatically.
 *
 * `alert*Bg`/`alert*Fg` mirror every tone in `ui/Alert.tsx`'s `TONES` map
 * the same way, for the same reason (literal Tailwind hex, not vars — see
 * that file's comment for why its hexes deliberately differ from Badge's
 * `success`/`info`). Recompute by hand if `ui/Alert.tsx`'s TONES map
 * changes.
 */
export const THEME_TOKENS = {
  light: {
    surface: '#ffffff',
    surfaceSunken: '#faf9f7',
    surfaceRaised: '#ffffff',
    border: '#e7e5e4',
    text: '#1c1917',
    textMuted: '#78716c',
    accent: '#0f766e',
    accentFg: '#ffffff',
    // DataTable's selected-row wash. Precomputed 10% `accent` blended over
    // `surface` — NOT a Tailwind opacity modifier (doesn't compile for a
    // CSS-variable-based color). Must be recomputed by hand if `accent` or
    // `surface` changes, since nothing derives it automatically.
    rowSelected: '#e7f1f1',
    // Button `warning` variant fill — amber-700, not amber-600 (which fails
    // AA against white at 3.19:1). Fixed across themes, see class comment.
    warning: '#b45309',
    warningFg: '#ffffff',
    // Badge tones — literal Tailwind palette values, see class comment above.
    badgeNeutralBg: '#f5f5f4',   // stone-100
    badgeNeutralFg: '#44403c',   // stone-700
    badgeSuccessBg: '#ccfbf1',   // teal-100
    badgeSuccessFg: '#134e4a',   // teal-900
    badgeWarningBg: '#fef3c7',   // amber-100
    badgeWarningFg: '#78350f',   // amber-900
    badgeDangerBg: '#fee2e2',    // red-100
    badgeDangerFg: '#7f1d1d',    // red-900
    badgeInfoBg: '#e0f2fe',      // sky-100
    badgeInfoFg: '#0c4a6e',      // sky-900
    badgePurpleBg: '#f3e8ff',    // purple-100
    badgePurpleFg: '#581c87',    // purple-900
    badgeOrangeBg: '#ffedd5',    // orange-100
    badgeOrangeFg: '#7c2d12',    // orange-900
    // Alert tones — literal Tailwind palette values, see ui/Alert.tsx comment.
    alertNeutralBg: '#f5f5f4',  // stone-100
    alertNeutralFg: '#44403c',  // stone-700
    alertSuccessBg: '#f0fdf4',  // green-50
    alertSuccessFg: '#14532d',  // green-900
    alertWarningBg: '#fffbeb',  // amber-50
    alertWarningFg: '#78350f',  // amber-900
    alertDangerBg: '#fef2f2',   // red-50
    alertDangerFg: '#991b1b',   // red-800
    alertInfoBg: '#eff6ff',     // blue-50
    alertInfoFg: '#1e40af',     // blue-800
    // Inline status TEXT on a neutral surface — no fill, so unlike Badge/
    // Alert these are foreground-only and are paired against
    // surface/surfaceSunken/surfaceRaised in contrast.test.ts. Used by
    // ReportSchedulePanel's last_status cell (`sent` / `error`) and
    // MeterConfig's inline "Saved"/error captions.
    // green-700, NOT green-600: green-600 (#16a34a) is 3.30:1 on white —
    // fine for LARGE text (MeterConfig's text-2xl KPI keeps it) but below
    // the 4.5:1 normal-text floor these captions need.
    statusOkText: '#15803d',     // green-700
    statusErrorText: '#dc2626',  // red-600
  },
  dark: {
    surface: '#1c1917',
    surfaceSunken: '#0c0a09',
    surfaceRaised: '#292524',
    border: '#44403c',
    text: '#fafaf9',
    textMuted: '#a8a29e',
    accent: '#2dd4bf',
    accentFg: '#042f2e',
    // Same 10% `accent`-over-`surface` blend as light, recomputed for dark's
    // accent/surface values. Recompute if either changes.
    rowSelected: '#1e2c28',
    // Same fixed warning fill as light — see class comment.
    warning: '#b45309',
    warningFg: '#ffffff',
    // Badge tones (dark:) — `badgeNeutralBg` is stone-700, NOT stone-800:
    // stone-800 (#292524) is byte-identical to `surfaceRaised` above, which
    // is the bug this token map exists to keep policed. Every other tone's
    // dark bg differs from `surfaceRaised` by construction.
    badgeNeutralBg: '#44403c',   // stone-700 (fixed from stone-800)
    badgeNeutralFg: '#d6d3d1',   // stone-300
    badgeSuccessBg: '#134e4a',   // teal-900
    badgeSuccessFg: '#ccfbf1',   // teal-100
    badgeWarningBg: '#78350f',   // amber-900
    badgeWarningFg: '#fef3c7',   // amber-100
    badgeDangerBg: '#7f1d1d',    // red-900
    badgeDangerFg: '#fee2e2',    // red-100
    badgeInfoBg: '#0c4a6e',      // sky-900
    badgeInfoFg: '#e0f2fe',      // sky-100
    badgePurpleBg: '#581c87',    // purple-900
    badgePurpleFg: '#f3e8ff',    // purple-100
    badgeOrangeBg: '#7c2d12',    // orange-900
    badgeOrangeFg: '#ffedd5',    // orange-100
    // Alert tones (dark:) — see ui/Alert.tsx comment. None of these land on
    // `surfaceRaised` dark (#292524 / stone-800): `alertNeutralBg` is
    // stone-700 (not stone-800) for the same reason Badge's neutral moved
    // off it, and every colour tone's dark bg (green/amber/red/blue-900) is
    // nowhere near stone's hue, so it differs by construction, not by luck.
    alertNeutralBg: '#44403c',  // stone-700 (fixed from stone-800 — see comment)
    alertNeutralFg: '#d6d3d1',  // stone-300
    alertSuccessBg: '#14532d',  // green-900
    alertSuccessFg: '#dcfce7',  // green-100
    alertWarningBg: '#78350f',  // amber-900
    alertWarningFg: '#fef3c7',  // amber-100
    alertDangerBg: '#7f1d1d',   // red-900
    alertDangerFg: '#fee2e2',   // red-100
    alertInfoBg: '#1e3a8a',     // blue-900
    alertInfoFg: '#dbeafe',     // blue-100
    // "Critical" fills — literal dark: Tailwind utilities (not vars, same
    // reason as Badge/Alert above) used at a handful of call sites that each
    // hand-state their own AA math in a code comment: BillRunDetail.tsx's
    // pre-check-flags box (amber-950) and BillVerification.tsx's upload
    // dropzone hover / pocket-picker selected row + CreateBillRunDialog.tsx's
    // twin dropzone hover (indigo-900, the most fragile pairing on the
    // branch — textMuted clears AA by ~0.03). Those comments claimed this
    // suite polices them; it didn't, until these two entries and the
    // dark-only describe block below were added. No `light` entry: both
    // fills are only ever applied via a `dark:` class, so there is no
    // light-mode pairing to guard (same reasoning as C3's `rowSelected`
    // dark-only assertion below).
    criticalAmber950: '#451a03',   // amber-950
    criticalIndigo900: '#312e81',  // indigo-900
    // Inline status text (dark:) — the -400 shades the `error` branch
    // already used, now matched by the `sent` branch. See the light entries.
    statusOkText: '#4ade80',     // green-400
    statusErrorText: '#f87171',  // red-400
  },
} as const;

export function resolveInitialTheme(stored: string | null, prefersDark: boolean): Theme {
  if (stored === 'light' || stored === 'dark') return stored;
  return prefersDark ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
}
